import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import { Expense, Category } from '../types';
import { CURRENCIES } from '../config/currencies';
import CurrencySelect from './CurrencySelect';
import ToggleSwitch from './ToggleSwitch';

interface EditExpenseModalProps {
  isOpen: boolean;
  expense: Expense;
  onClose: () => void;
  onSubmit: (expense: Expense) => void;
  loading?: boolean;
  categories?: Category[];
  currencies?: string[];
  categoriesLoading?: boolean;
}

const EditExpenseModal: React.FC<EditExpenseModalProps> = ({
  isOpen,
  expense,
  onClose,
  onSubmit,
  loading = false,
  categories = [],
  currencies = Object.keys(CURRENCIES),
  categoriesLoading = false
}) => {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [isBigExpense, setIsBigExpense] = useState(false);

  useEffect(() => {
    if (expense) {
      setName(expense.name);
      // Round to 2 decimal places for display
      const amountValue = typeof expense.amount === 'string' ? parseFloat(expense.amount) : expense.amount;
      setAmount(amountValue.toFixed(2));
      setCurrency(expense.currency);
      setCategoryId(expense.category?.id || '');
      setDescription(expense.description || '');
      setCreatedAt(format(new Date(expense.created_at), 'yyyy-MM-dd'));
      setIsBigExpense(expense.is_big_expense || false);
    }
  }, [expense]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const selectedCategory = categories.find(cat => cat.id === categoryId);
    const updatedExpense: Expense = {
      ...expense,
      name,
      amount: parseFloat(amount),
      currency,
      category: selectedCategory || expense.category,
      description,
      created_at: createdAt,
      is_big_expense: isBigExpense
    };

    onSubmit(updatedExpense);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="expense-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="heading-2" style={{ margin: 0 }}>Edit Expense</h2>
          <button
            onClick={onClose}
            className="btn btn-icon btn-secondary"
            disabled={loading}
            style={{ padding: 'var(--spacing-sm)', width: '32px', height: '32px' }}
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <ToggleSwitch
              checked={isBigExpense}
              onChange={setIsBigExpense}
              label="One-time big expense"
              disabled={loading}
            />

            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Coffee at Starbucks"
                maxLength={150}
                disabled={loading}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 'var(--spacing-md)' }}>
              <div className="form-group">
                <label className="form-label">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-input"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Currency</label>
                <CurrencySelect
                  value={currency}
                  onChange={setCurrency}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Category *</label>
              <select
                className="form-select"
                value={categoryId}
                onChange={(e) => setCategoryId(Number(e.target.value))}
                disabled={loading || categoriesLoading}
                required
              >
                {categoriesLoading ? (
                  <option value="">Loading categories...</option>
                ) : (
                  <>
                    <option value="">Select a category</option>
                    {categories.map((cat) => {
                      const parent = cat.parent_id
                        ? categories.find(c => c.id === cat.parent_id)
                        : null;
                      return (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}{parent ? ` (${parent.name})` : ''}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Date *</label>
              <input
                type="date"
                className="form-input"
                value={createdAt}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setCreatedAt(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional notes about this expense"
                maxLength={350}
                rows={3}
                disabled={loading}
                style={{ resize: 'vertical', minHeight: '80px' }}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !name.trim() || !amount.trim() || !categoryId || !createdAt}
            >
              {loading ? (
                <>
                  <div className="spinner" style={{ width: '16px', height: '16px' }} />
                  Updating...
                </>
              ) : (
                'Update Expense'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditExpenseModal;
